import {Dispatch, FC, SetStateAction, useEffect, useState} from 'react'
import {InvoiceEditModalHeader} from './InvoiceEditModalHeader'
import {InvoiceEditModalFormWrapper, useEditedInvoiceQuery} from './InvoiceEditModalFormWrapper'
import {getExtraSetting, saveExtraSetting} from '../../../../../../_metronic/helpers'
import {useListView} from '../core/ListViewProvider'
import {useAuth} from '../../../../auth'
import {FieldHelpProvider} from '../../../../../../_metronic/helpers/components/FieldHelp'
import {resolveInvoiceFieldHelp} from '../core/invoiceFieldHelpCatalog'
import './InvoiceResponsive.css'
import {isOnPremisesInstallation} from '../../../../auth/core/onPremisesAccess'
import {resolveEditFormType} from '../invoiceAdvancedCostForm/advancedCostFormPolicy'
import {isProformaInvoice, opensNewProforma} from '../core/invoiceProforma'
import {useUnsavedWork} from '../../../shared/business-switcher/unsavedWork'

type props = {
  invoiceType: {
    invoiceType: number
    setInvoiceType: Dispatch<SetStateAction<number>>
  }
  invoiceTemplate: {
    invoiceTemplate: number
    setInvoiceTemplate: Dispatch<SetStateAction<number>>
  }
  title: {
    title: string
    setTitle: Dispatch<SetStateAction<string>>
  }
  closeFn?: () => void
}

const InvoiceEditModal: FC<props> = ({title, invoiceType, invoiceTemplate, closeFn}) => {
  const {variableD, itemIdForUpdate, newProforma} = useListView()
  const {currentUser, setCurrentUser, saveAuth} = useAuth()
  const {data: editedInvoice} = useEditedInvoiceQuery(itemIdForUpdate)
  // A proforma is written in the full form only: its validity and terms live there.
  const proformaMode =
    isProformaInvoice(editedInvoice) ||
    opensNewProforma({newProforma, itemIdForUpdate, referralAction: variableD})
  const hasFixedFullForm =
    variableD === 4 || variableD === 5 || isOnPremisesInstallation(currentUser) || proformaMode
  // Choosing another business while the form is open asks first (the form would be lost).
  useUnsavedWork('فرم صورتحساب')
  useEffect(() => {
    document.body.classList.add('modal-open')
    return () => {
      document.body.classList.remove('modal-open')
      invoiceType.setInvoiceType(0)
      invoiceTemplate.setInvoiceTemplate(0)
      title.setTitle('')
      if (closeFn) closeFn()
    }
  }, [])

  const [formType, setFormType] = useState(0)
  const [oldFormType, setOldFormType] = useState(0)
  const [first, setFirst] = useState(true)

  useEffect(() => {
    if (hasFixedFullForm) {
      if (formType !== 0) setFormType(0)
      if (first) setFirst(false)
    } else {
      if (first) {
        setFormType(currentUser?.inmd || 0)
        setOldFormType(currentUser?.inmd || 0)
        setFirst(false)
      } else {
        if (formType != oldFormType) {
          saveExtraSetting('addInvoiceFormType', formType).then((response) => {
            setOldFormType(formType)
            saveAuth(response)
            setCurrentUser(response)
          })
        }
      }
    }
  }, [formType])

  // An existing invoice the advanced form may not rebuild opens in the full form; the form the
  // user chose stays saved for new invoices.
  const effectiveFormType = proformaMode
    ? 0
    : resolveEditFormType(formType, editedInvoice, variableD)
  const fullFormForced = !proformaMode && effectiveFormType !== (Number(formType) || 0)

  return (
    <FieldHelpProvider resolver={resolveInvoiceFieldHelp}>
      <div
        className='modal fade show d-block mp-invoice-issue-modal'
        id='kt_modal_add_invoice'
        role='dialog'
        tabIndex={-1}
        aria-modal='true'
      >
        {/* begin::Modal dialog */}
        <div
          className={`modal-dialog modal-dialog-centered modal-xl modal-fullscreen-lg-down mp-invoice-form-dialog ${
            effectiveFormType === 1 || effectiveFormType === 2 || effectiveFormType === 3
              ? 'mp-invoice-form-dialog--compact'
              : 'mp-invoice-form-dialog--wide'
          }`}
        >
          {/* begin::Modal content */}
          <div className='modal-content mp-invoice-form-shell'>
            <InvoiceEditModalHeader
              title={title.title}
              closeFn={closeFn}
              formType={{
                formType: formType,
                setFormType: setFormType,
              }}
              lockedFullForm={fullFormForced || proformaMode}
            />
            {/* begin::Modal body */}
            <div className='modal-body scroll-y mp-invoice-form-body'>
              {fullFormForced && (
                <div className='alert alert-light-info mb-5' role='status'>
                  این صورتحساب در فرم کامل باز شد؛ فرم قیمت تمام شده پیشرفته کالاها را دوباره
                  می‌سازد و فقط صورتحساب ارسال‌نشده‌ای را ویرایش می‌کند که خود این فرم، اکسل
                  صورتحساب خودکار یا صفحهٔ کارت‌خوان ساخته باشد.
                </div>
              )}
              <InvoiceEditModalFormWrapper
                title={title}
                invoiceType={invoiceType}
                invoiceTemplate={invoiceTemplate}
                closeFn={closeFn}
                formType={{
                  formType: effectiveFormType,
                  setFormType: setFormType,
                }}
              />
            </div>
            {/* end::Modal body */}
          </div>
          {/* end::Modal content */}
        </div>
        {/* end::Modal dialog */}
      </div>
      {/* begin::Modal Backdrop */}
      <div className='modal-backdrop fade show'></div>
      {/* end::Modal Backdrop */}
    </FieldHelpProvider>
  )
}

export {InvoiceEditModal}
